/**
 * An administrator creates an account directly: name, email, role, and the
 * customers it may work on. The account is active at once; the server
 * answers with a temporary password, handed back to the page, which reveals
 * it exactly once.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { api, ApiError, type AdminCustomer, type Role } from "../lib/api";

const ROLES: Role[] = ["operator", "approver", "sysadmin"];

export function NewUserDialog({
  customers,
  onClose,
  onCreated,
}: {
  customers: AdminCustomer[];
  onClose: () => void;
  onCreated: (email: string, temporaryPassword: string) => Promise<void>;
}) {
  const { t } = useTranslation();
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<Role>("operator");
  const [all, setAll] = useState(false);
  const [chosen, setChosen] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const isSysadmin = role === "sysadmin";
  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const ready = fullName.trim().length > 0 && emailOk && !busy;

  const toggle = (id: string) =>
    setChosen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  async function create() {
    setBusy(true);
    setError("");
    try {
      const result = await api.adminCreateUser({
        email: email.trim(),
        full_name: fullName.trim(),
        role,
        all_customers: isSysadmin ? false : all,
        customer_ids: isSysadmin || all ? [] : [...chosen],
      });
      await onCreated(result.user.email, result.temporary_password);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("admin.actionFailed"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t("admin.newUserTitle")}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>{t("admin.newUserBody")}</DialogContentText>
        <Stack spacing={2}>
          <TextField
            label={t("admin.fullName")}
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            autoFocus
            fullWidth
            inputProps={{ maxLength: 120 }}
          />
          <TextField
            label={t("admin.email")}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            fullWidth
            error={email.length > 0 && !emailOk}
          />
          <TextField
            select
            label={t("admin.role")}
            value={role}
            onChange={(e) => setRole(e.target.value as Role)}
            fullWidth
          >
            {ROLES.map((name) => (
              <MenuItem key={name} value={name}>
                {t(`auth.role.${name}`, name)}
              </MenuItem>
            ))}
          </TextField>

          <div>
            <Typography variant="subtitle2" gutterBottom>
              {t("customers.colCustomers")}
            </Typography>
            {isSysadmin ? (
              <Typography variant="body2" color="text.secondary">
                {t("admin.sysadminCustomers")}
              </Typography>
            ) : (
              <>
                <FormControlLabel
                  control={<Checkbox checked={all} onChange={(e) => setAll(e.target.checked)} />}
                  label={t("customers.assignAll")}
                />
                <List dense disablePadding>
                  {customers.map((c) => (
                    <ListItem key={c.id} disablePadding>
                      <ListItemButton disabled={all} onClick={() => toggle(c.id)} dense>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <Checkbox
                            edge="start"
                            size="small"
                            checked={all || chosen.has(c.id)}
                            tabIndex={-1}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={c.name}
                          secondary={
                            c.status === "inactive" ? t("customers.status.inactive") : c.code
                          }
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </>
            )}
          </div>
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          {t("common.cancel")}
        </Button>
        <Button variant="contained" onClick={() => void create()} disabled={!ready}>
          {t("admin.create")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
